import React, { useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { translations } from "../data/mock";
import Turnstile from "../components/Turnstile";
import { contactAPI } from "../services/api";
import "../styles/portfolio.css";

export default function Contact() {
  const { language, t } = useLanguage();
  const turnstileRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    consent: false,
    website: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: "",
  });

  const labels = {
    infoEmail: { it: "EMAIL", en: "EMAIL" },
    infoStudio: { it: "STUDIO", en: "STUDIO" },
    studioValue: { it: "Málaga, Spagna", en: "Málaga, Spain" },
    availability: {
      it: "Disponibile per collaborazioni.",
      en: "Available for collaborations.",
    },
    consent: {
      it: "Acconsento al trattamento dei dati per ricevere una risposta al mio messaggio.",
      en: "I consent to the processing of my data so I can receive a reply to my message.",
    },
    consentNote: {
      it: "I dati saranno usati solo per gestire questa richiesta; questo modulo non iscrive alla newsletter.",
      en: "Your data will only be used to handle this request; this form does not subscribe you to a newsletter.",
    },
    formTitle: {
      it: "Scrivimi",
      en: "Write to me",
    },
    firstName: {
      it: "Nome",
      en: "First name",
    },
    lastName: {
      it: "Cognome",
      en: "Last name",
    },
    email: {
      it: "Email",
      en: "Email",
    },
    message: {
      it: "Messaggio",
      en: "Message",
    },
    firstNamePlaceholder: {
      it: "Il tuo nome",
      en: "Your first name",
    },
    lastNamePlaceholder: {
      it: "Il tuo cognome",
      en: "Your last name",
    },
    emailPlaceholder: {
      it: "La tua email",
      en: "Your email",
    },
    messagePlaceholder: {
      it: "Scrivi qui il tuo messaggio",
      en: "Write your message here",
    },
    send: {
      it: "Invia",
      en: "Send",
    },
    sending: {
      it: "Invio...",
      en: "Sending...",
    },
    success: {
      it: "Messaggio inviato con successo.",
      en: "Message sent successfully.",
    },
    error: {
      it: "Si è verificato un errore. Riprova.",
      en: "Something went wrong. Please try again.",
    },
    required: {
      it: "Compila tutti i campi.",
      en: "Please fill in all fields.",
    },
    turnstileRequired: {
      it: "Completa la verifica antispam.",
      en: "Please complete the spam protection check.",
    },
    rateLimit: {
      it: "Hai inviato troppe richieste. Attendi qualche minuto e riprova.",
      en: "You have sent too many requests. Please wait a few minutes and try again.",
    },
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (status.error) {
      setStatus((prev) => ({ ...prev, error: "" }));
    }
  };

  const handleTurnstileToken = (token) => {
    setTurnstileToken(token);
    if (token && status.error) {
      setStatus((prev) => ({ ...prev, error: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();
    if (!formData.consent) {
      setStatus({
        loading: false,
        success: false,
        error: t(labels.required),
      });
      return;
    }

    if (!firstName || !lastName || !email || !message) {
      setStatus({
        loading: false,
        success: false,
        error: t(labels.required),
      });
      return;
    }

    if (!turnstileToken) {
      setStatus({
        loading: false,
        success: false,
        error: t(labels.turnstileRequired),
      });
      return;
    }

    try {
      setStatus({
        loading: true,
        success: false,
        error: "",
      });

      await contactAPI.submit({
        name: `${firstName} ${lastName}`.trim(),
        email,
        message,
        consent: formData.consent,
        website: formData.website,
        turnstile_token: turnstileToken,
      });

      setStatus({
        loading: false,
        success: true,
        error: "",
      });

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        message: "",
        consent: false,
        website: "",
      });
      turnstileRef.current?.reset();
    } catch (error) {
      turnstileRef.current?.reset();
      setStatus({
        loading: false,
        success: false,
        error: error.status === 429 ? t(labels.rateLimit) : t(labels.error),
      });
    }
  };

  return (
    <div className="page-offset">
      <section className="section-spacing">
        <div className="container" style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "var(--spacing-2xl)" }}>
            <h1 className="section-title">{t(translations.contact.title)}</h1>
          </div>

          <div className="contact-layout">
            <div className="contact-info">
              <div>
                <span className="small-text contact-label">{t(labels.infoEmail)}</span>
                <a className="body-text contact-mail" href="mailto:info@evatazzari.com">
                  info@evatazzari.com
                </a>
              </div>

              <div>
                <span className="small-text contact-label">{t(labels.infoStudio)}</span>
                <div className="body-text">{t(labels.studioValue)}</div>
              </div>

              <div className="body-text" style={{ maxWidth: 320 }}>
                {t(labels.availability)}
              </div>
            </div>

            <div>
              <div className="contact-form-header">
                <span className="small-text contact-label">{t(labels.formTitle)}</span>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-honeypot" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    name="website"
                    type="text"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    maxLength={200}
                  />
                </div>

                <div className="contact-form-row">
                  <div className="contact-field">
                    <label htmlFor="firstName" className="small-text">
                      {t(labels.firstName)}
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      className="contact-input"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={t(labels.firstNamePlaceholder)}
                      maxLength={80}
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="lastName" className="small-text">
                      {t(labels.lastName)}
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      className="contact-input"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder={t(labels.lastNamePlaceholder)}
                      maxLength={80}
                      required
                    />
                  </div>
                </div>

                <div className="contact-field">
                  <label htmlFor="email" className="small-text">
                    {t(labels.email)}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="contact-input"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t(labels.emailPlaceholder)}
                    maxLength={254}
                    required
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="message" className="small-text">
                    {t(labels.message)}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="contact-textarea"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t(labels.messagePlaceholder)}
                    rows={7}
                    maxLength={5000}
                    required
                  />
                </div>
                <div className="contact-field" style={{ marginTop: 10 }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      style={{ marginTop: 4 }}
                      required
                    />
                    <div>
                      <span className="small-text" style={{ fontSize: 11 }}>
                        {t(labels.consent)}
                      </span>
                      <div
                        className="small-text"
                        style={{ fontSize: 11, opacity: 0.6 }}
                      >
                        {t(labels.consentNote)}
                      </div>
                    </div>
                  </label>
                </div>

                <Turnstile
                  ref={turnstileRef}
                  action="contact"
                  language={language}
                  onTokenChange={handleTurnstileToken}
                />

                <div className="contact-actions">
                  <button
                    type="submit"
                    disabled={status.loading}
                    className="btn btn-outline"
                    style={{
                      minWidth: 160,
                      justifyContent: "center",
                      opacity: status.loading ? 0.7 : 1,
                    }}
                  >
                    {status.loading ? t(labels.sending) : t(labels.send)}
                  </button>

                  {status.success && (
                    <span className="small-text contact-status-success">
                      {t(labels.success)}
                    </span>
                  )}

                  {status.error && (
                    <span className="small-text contact-status-error">
                      {status.error}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
